import { PageHeader } from "@nt/ui/page-header";
import { PillTabPanel, PillTabs } from "@nt/ui/pill-tabs";
import { Navigate, useNavigate, useParams } from "react-router";

import type { Session } from "@/lib/queries";
import { NotificationsTab } from "@/pages/settings/notifications";
import { PayoutMethodTab } from "@/pages/settings/payout-method";
import { ProfileTab } from "@/pages/settings/profile";
import { TaxFormTab } from "@/pages/settings/tax-form";
import { TermsTab } from "@/pages/settings/terms";

export const SETTINGS_TABS = [
  { value: "profile", label: "Profile" },
  { value: "payout-method", label: "Payout method" },
  { value: "tax-form", label: "Tax form" },
  { value: "notifications", label: "Notifications" },
  { value: "terms", label: "Program terms" },
] as const;

export type SettingsTab = (typeof SETTINGS_TABS)[number]["value"];

const isTab = (v: string | undefined): v is SettingsTab =>
  SETTINGS_TABS.some((t) => t.value === v);

/** `/settings` is Profile; every other tab has its own `/settings/:tab` URL. */
const settingsPath = (tab: SettingsTab) =>
  tab === "profile" ? "/settings" : `/settings/${tab}`;

/** `/settings/:tab?`: one header, the tab row, the open tab's panel. */
export function SettingsPage({
  session,
  readOnly,
}: {
  session: Session;
  readOnly: boolean;
}) {
  const { tab: param } = useParams();
  const navigate = useNavigate();
  if (param !== undefined && (!isTab(param) || param === "profile")) {
    return <Navigate to="/settings" replace />;
  }
  const tab: SettingsTab = param ?? "profile";
  return (
    <>
      <PageHeader
        title="Settings"
        description="Your profile, how we pay you, and the program terms."
      />
      <div className="flex flex-col gap-4">
        <PillTabs
          aria-label="Settings"
          idPrefix="settings"
          testIdPrefix="settings-tab"
          items={SETTINGS_TABS}
          value={tab}
          onChange={(v) => {
            if (isTab(v)) navigate(settingsPath(v));
          }}
        />
        <PillTabPanel
          idPrefix="settings"
          value={tab}
          className="mt-0 flex flex-col gap-6"
        >
          {tab === "profile" ? (
            <ProfileTab session={session} readOnly={readOnly} />
          ) : tab === "payout-method" ? (
            <PayoutMethodTab readOnly={readOnly} email={session.email} />
          ) : tab === "tax-form" ? (
            <TaxFormTab readOnly={readOnly} />
          ) : tab === "notifications" ? (
            <NotificationsTab session={session} readOnly={readOnly} />
          ) : (
            <TermsTab />
          )}
        </PillTabPanel>
      </div>
    </>
  );
}
